import React, { useState, useEffect, useRef } from 'react';
import { GoMute, GoUnmute } from 'react-icons/go';
import { FaPlay } from 'react-icons/fa';
import { useDisplayWidgetContext } from '../DisplayWidget/utils/context';
import { getCloudflareWidth, getCloudflareSize } from '../../utils/rounding';

export const VideoPlayer = ({
  videoSource,
  showMute,
  autoPlay,
  playFunctionality,
  preload,
  previewUrl,
  ...props
}: any) => {
  const [isMuted, setIsMuted] = useState(true);
  const [videoPlaying, setVideoPlaying] = useState(autoPlay);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [posterUrl, setPosterUrl] = useState<string>(''); // NEW STATE
  const { layout, isOpen, userId } = useDisplayWidgetContext();
  const toggleMute = () => {
    setIsMuted((prevState) => !prevState);
  };

  const videoRef = useRef<HTMLVideoElement>(null);
  const [finalVideoUrl, setFinalVideoUrl] = useState<string>('');

  useEffect(() => {
    const el = videoRef.current;
    if (!el) return;

    // Build the base URL (your existing logic)
    const rawUrl = props?.poster
      ? resolveVideoUrl(videoSource).replace('#t=0.2', '')
      : resolveVideoUrl(videoSource)?.includes('#t=0.2')
      ? resolveVideoUrl(videoSource)
      : `${resolveVideoUrl(videoSource)}#t=0.2`;

    // Apply dynamic width/height transformation
    const transformed = resolveVideoUrl(rawUrl, el);
    setFinalVideoUrl(transformed);
  }, [videoSource, props?.poster]);

  const roundToNearest10 = (value: number) => {
    const remainder = value % 10;
    if (remainder < 5) return value - remainder; // 604 → 600, 608 → 600
    return value + (10 - remainder); // 612 → 610
  };

  // Helper: choose correct CDN based on domain
  const resolveVideoUrl = (url: string, videoEl?: HTMLVideoElement) => {
    const host = window.location.hostname;

    // Only apply transformation for fixmycurls.com
    if (!host.includes('fixmycurls111.com')) return url;

    // Only transform if the URL is from idukki-cdn.com
    if (!url.includes('idukki-cdn.com')) return url;

    if (!videoEl) return url; // safety fallback

    const width = roundToNearest10(videoEl.clientWidth || videoEl.offsetWidth);
    const height = roundToNearest10(videoEl.clientHeight || videoEl.offsetHeight);

    // Build Cloudflare Image Resizing URL
    const prefix = `https://idukki-cdn.com/cdn-cgi/media/width=${width},height=${height}`;

    // Remove existing domain prefix
    const path = url.replace('https://idukki-cdn.com', '');

    return `${prefix}${path}`;
  };

  const updateProgressBar = () => {
    if (videoRef.current) {
      setCurrentTime(videoRef.current.currentTime);
      setDuration(videoRef.current.duration);
    }
  };

  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.addEventListener('timeupdate', updateProgressBar);
    }

    return () => {
      if (videoRef.current) {
        videoRef.current.removeEventListener('timeupdate', updateProgressBar);
      }
    };
  }, []);

  // CHANGE: ResizeObserver to dynamically set Cloudflare poster URL
  useEffect(() => {
    if (!videoRef.current) return;

    const updatePoster = (width: number) => {
      if (props.poster) {
        //temp: for wyo.in let it autoplay gif but later we control via property
        const host = window.location.hostname;
        if (host.includes('wyo.in') || props.poster.includes('.mp4')) {
          videoRef.current?.setAttribute('data-poster', props.poster);
          return;
        }
        try {
          const parsed = new URL(props.poster);

          // Only apply Cloudflare resize if domain is idukki-cdn.com
          if (parsed.hostname.includes('idukki-cdn.com')) {
            const cfPoster = `${parsed.origin}/cdn-cgi/image/width=${getCloudflareWidth(width)},quality=90,format=auto${
              parsed.pathname
            }`;
            videoRef.current?.setAttribute('data-poster', cfPoster);
          } else {
            // Fallback: keep original poster
            videoRef.current?.setAttribute('data-poster', props.poster);
          }
        } catch {
          // Fallback: if URL parsing fails, just keep original
          videoRef.current?.setAttribute('data-poster', props.poster);
        }
      }
    };

    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        updatePoster(entry.contentRect.width);
      }
    });

    observer.observe(videoRef.current);

    return () => observer.disconnect();
  }, [props.poster]);

  const calculateProgress = () => {
    if (duration > 0) {
      return (currentTime / duration) * 100;
    }
    return 0;
  };

  const handlePlay = (e: React.SyntheticEvent) => {
    e.preventDefault();
    if (videoPlaying) {
      videoRef.current.pause();
    } else {
      videoRef.current.play();
    }
    setVideoPlaying((prevState) => !prevState);
  };

  useEffect(() => {
    setVideoPlaying(false);
  }, [videoSource]);

  const isIOS = () => {
    return /iPad|iPhone|iPod/.test(navigator.userAgent);
  };
  // useEffect(() => {
  //   if (preload === 'auto' && videoRef.current) {
  //     const videoElement = videoRef.current;

  //     if (videoElement.paused) {
  //       videoElement.play().catch((err) => {
  //         console.error('Error playing video:', err);
  //       });
  //     }
  //   }
  // }, [preload]);

  useEffect(() => {
    const videoElement = videoRef.current;
    if (!videoElement) return;

    const loadPoster: IntersectionObserverCallback = (entries, observer) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const posterUrl = videoElement.getAttribute('data-poster');
          if ((posterUrl.includes('.mp4') || previewUrl) && !isOpen) {
            videoElement.src = previewUrl || posterUrl;
            videoElement.autoplay = true;
          } else if (posterUrl) {
            videoElement.poster = posterUrl; // set poster lazily
          }
          observer.unobserve(videoElement); // stop once loaded
        }
      });
    };

    const observer = new IntersectionObserver(loadPoster, { threshold: 0.01 });
    observer.observe(videoElement);

    // Immediate check in case it's already visible
    const rect = videoElement.getBoundingClientRect();
    const inView = rect.top < window.innerHeight && rect.bottom > 0 && rect.left < window.innerWidth && rect.right > 0;

    if (inView) {
      const posterUrl = videoElement.getAttribute('data-poster');
      if ((posterUrl.includes('.mp4') || previewUrl) && !isOpen) {
        videoElement.src = previewUrl || posterUrl;
        videoElement.autoplay = true;
      } else if (posterUrl) {
        videoElement.poster = posterUrl;
      }
      observer.unobserve(videoElement);
    }

    return () => {
      observer.unobserve(videoElement);
    };
  }, [videoSource]);

  const checkIfPlaying = () => {
    if (videoRef.current) {
      setVideoPlaying(!videoRef.current.paused);
    }
  };

  const handleVideoError = (e) => {
    const video = e.currentTarget;
  
    // Prevent infinite loop
    if (video.__idukkiFallbackApplied) return;
    video.__idukkiFallbackApplied = true;
  
    const fixUrl = (url) =>
      url?.includes('idukki-cdn.com')
        ? url.replace('https://idukki-cdn.com', 'https://assets.idukki.io')
        : url;
  
    video.onerror = null; // stop further bubbling
    video.src = fixUrl(video.src);
    video.poster = fixUrl(video.poster);
  };

  const milestonesRef = React.useRef(new Set<number>());
  const lastTimeRef = React.useRef(0);
  const milestoneInterval = 20;

  const trackVideoQuartile = (percentage: number) => {
    if (!props?.videoId) return;

    const totalDuration = Math.round(videoRef.current?.duration || 0);

    const trackingUrl = `https://track.idukki.io/video?postId=${props.videoId}&userId=${userId}&total=${totalDuration}&current=${percentage}`;

    try {
      fetch(trackingUrl, { method: 'GET' }).catch((err) => {
        console.warn('Video tracking API error:', err);
      });
    } catch (error) {
      console.warn('Video tracking error:', error);
    }
  };

  const handleTimeUpdate = (e: React.SyntheticEvent<HTMLVideoElement>) => {
    const video = e.currentTarget;
    if (!video.duration) return;
    if (video.currentTime < lastTimeRef.current) {
      milestonesRef.current.clear();
    }
    lastTimeRef.current = video.currentTime;
    const percent = (video.currentTime / video.duration) * 100;
    if (percent >= 99 && !milestonesRef.current.has(100)) {
      milestonesRef.current.add(100);
      trackVideoQuartile(100);
      return;
    }
    const bucket = Math.floor(percent / milestoneInterval) * milestoneInterval;
    if (bucket >= milestoneInterval && bucket < 100 && !milestonesRef.current.has(bucket)) {
      milestonesRef.current.add(bucket);
      trackVideoQuartile(bucket);
    }
  };
  return (
    <React.Fragment>
      <style>{`
        .idk_play_icon path {
          stroke-width: 1;
        }
        .idk_play_btn  {
          color: white;
        }
        .idk_play_btn_container {
          ${layout?.videoSettings?.playBtnContainerCss}
        }
         .idk_play_btn_container .idk_play_btn {
          
          ${layout?.videoSettings?.playBtnCss}
          }
      `}</style>
      <idk-div className="relative" style={{ ...props.style }}>
        {showMute && (
          <idk-div
            class="idk_mute_modal_icon zid-bg-[#0000004d] zid-rounded zid-flex zid-items-center zid-justify-center zid-h-[35px] zid-w-[35px]"
            style={{ position: 'absolute', top: '15px', left: '10px', zIndex: 99999 }}
            onClick={toggleMute}
          >
            {isMuted ? <GoMute fontSize="18px" color="#ffffff" /> : <GoUnmute fontSize="18px" color="#ffffff" />}
          </idk-div>
        )}
        {layout.galleryView != 'stories' &&
          layout?.videoSettings?.showPlayBtn &&
          !videoPlaying &&
          (isOpen || !autoPlay) && (
            <idk-div
              style={{ zIndex: 1, position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)' }}
              onClick={(e: React.SyntheticEvent) => {
                if (playFunctionality == true) {
                  handlePlay(e);
                }
              }}
            >
              {layout?.videoSettings?.playBtnDesign == 'coloredBorderPlay' ? ( //@Jangrasourabh remove inline svg
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="80"
                  height="80"
                  fill="none"
                  viewBox="0 0 120 120"
                  className="mx-auto text-2xl text-gray-500"
                >
                  <path
                    fill="#090A0B99"
                    d="M60 114.546c30.125 0 54.546-24.421 54.546-54.546S90.125 5.455 60 5.455 5.455 29.875 5.455 60 29.875 114.546 60 114.546Z"
                  ></path>
                  <path stroke="url(#a)" strokeWidth="5.986" d="M60 5.455a54.546 54.546 0 0 1 47.236 27.272"></path>
                  <path stroke="url(#b)" strokeWidth="5.986" d="M107.236 32.727a54.54 54.54 0 0 1 0 54.546"></path>
                  <path stroke="url(#c)" strokeWidth="5.986" d="M107.236 87.273A54.548 54.548 0 0 1 60 114.546"></path>
                  <path stroke="url(#d)" strokeWidth="5.986" d="M60 114.546a54.55 54.55 0 0 1-47.236-27.273"></path>
                  <path stroke="url(#e)" strokeWidth="5.986" d="M12.764 87.273a54.545 54.545 0 0 1 0-54.545"></path>
                  <path stroke="url(#f)" strokeWidth="5.986" d="M12.764 32.727A54.546 54.546 0 0 1 60 5.455"></path>
                  <path
                    fill="#fff"
                    d="M50.387 85.167c-3.142 0-5.387-2.47-5.387-5.387V40.275C45 37.469 47.245 35 50.387 35c.898 0 1.908.224 2.806.786l32.322 19.752c1.571.898 2.581 2.694 2.581 4.49 0 1.907-1.01 3.703-2.581 4.6L53.193 84.382a5.33 5.33 0 0 1-2.806.786Z"
                  ></path>
                  <defs>
                    <linearGradient id="a" x1="60" x2="83.619" y1="5.455" y2="46.363" gradientUnits="userSpaceOnUse">
                      <stop offset="0" stopColor="#198DEE"></stop>
                      <stop offset="0.5" stopColor="#2A3CEF"></stop>
                    </linearGradient>
                    <linearGradient
                      id="b"
                      x1="107.236"
                      x2="107.236"
                      y1="32.727"
                      y2="87.273"
                      gradientUnits="userSpaceOnUse"
                    >
                      <stop offset="0" stopColor="#2A3CEF"></stop>
                      <stop offset="0.7" stopColor="#E124D9"></stop>
                    </linearGradient>
                    <linearGradient
                      id="c"
                      x1="107.236"
                      x2="83.617"
                      y1="87.273"
                      y2="128.181"
                      gradientUnits="userSpaceOnUse"
                    >
                      <stop offset="0" stopColor="#E124D9"></stop>
                      <stop offset="1" stopColor="#E52E56"></stop>
                    </linearGradient>
                    <linearGradient id="d" x1="60" x2="36.381" y1="114.546" y2="73.637" gradientUnits="userSpaceOnUse">
                      <stop offset="0.2" stopColor="#FFB400"></stop>
                      <stop offset="1" stopColor="#C4FF29"></stop>
                    </linearGradient>
                    <linearGradient id="e" x1="5.456" x2="5.456" y1="87.273" y2="32.727" gradientUnits="userSpaceOnUse">
                      <stop offset="0.1" stopColor="#C4FF29"></stop>
                      <stop offset="1" stopColor="#198DEE"></stop>
                    </linearGradient>
                    <linearGradient
                      id="f"
                      x1="12.764"
                      x2="36.383"
                      y1="32.727"
                      y2="-8.181"
                      gradientUnits="userSpaceOnUse"
                    >
                      <stop offset="0" stopColor="#198DEE"></stop>
                      <stop offset="1" stopColor="#198DEE"></stop>
                    </linearGradient>
                  </defs>
                </svg>
              ) : (
                <idk-div class="idk_play_btn_container">
                  <FaPlay className="idk_play_btn" fontSize={50} />
                </idk-div>
              )}
            </idk-div>
          )}
        {!isIOS() && (
          <video
            ref={videoRef}
            playsInline
            loop={props.onVideoComplete ? false : true}
            preload={props?.poster ? 'none' : preload}
            //disablePictureInPicture
            muted={isMuted}
            onPlay={checkIfPlaying}
            onPause={checkIfPlaying}
            data-poster={posterUrl || props?.poster || ''}
            autoPlay={autoPlay}
            onTimeUpdate={handleTimeUpdate}
            //poster={posterUrl || props?.poster || ''} // CHANGE: use dynamic poster
            onError={handleVideoError}
            onClick={(e: React.SyntheticEvent) => {
              if (playFunctionality == true) {
                handlePlay(e);
              }
            }}
            src={finalVideoUrl}
            style={{ position: 'relative', objectFit: 'cover', margin: 0, ...props.style }}
            onEnded={() => {
              if (props.onVideoComplete) {
                props.onVideoComplete();
              }
            }}
          >
            <track kind="captions" src="data:," label="English" default />
          </video>
        )}
        {isIOS() && (
          <video
            ref={videoRef}
            playsInline
            loop={props.onVideoComplete ? false : true}
            preload={props?.poster ? 'metadata' : preload} // for ioS only
            //disablePictureInPicture
            //poster={posterUrl || props?.poster || ''} // CHANGE: use dynamic poster
            muted={true} //just for iOS
            onPlay={checkIfPlaying}
            onPause={checkIfPlaying}
            onEnded={() => {
              if (props.onVideoComplete) {
                props.onVideoComplete();
              }
            }}
            onTimeUpdate={handleTimeUpdate}
            data-poster={posterUrl || props?.poster || ''}
            autoPlay={autoPlay}
            onClick={(e: React.SyntheticEvent) => {
              if (playFunctionality == true) {
                handlePlay(e);
              }
            }}
            style={{ position: 'relative', objectFit: 'cover', margin: 0, ...props.style }}
          >
            <source onError={handleVideoError} src={finalVideoUrl} type="video/mp4" />
            <track kind="captions" src="data:," label="English" default />
          </video>
        )}
        {isOpen && playFunctionality === true && duration > 0 && (
          <idk-div
            class="zid-bg-[#090a0b33] idk-progress-bar"
            style={{
              position: 'absolute',
              top: 0,
              width: '100%',
              borderRadius: '20px 20px 5px 5px',
              backdropFilter: 'blur(10px)',
            }}
          >
            <idk-div
              class="zid-transition"
              style={{
                width: `${calculateProgress()}%`,
                height: '5px',
                backgroundColor: 'white',
              }}
            />
          </idk-div>
        )}
      </idk-div>
    </React.Fragment>
  );
};
